import React, { useState, useEffect } from "react";
import DetailPlanView from "./DetailPlanView";
import { 
  Grid, TextField, Button, MenuItem, Select, FormControl, 
  InputLabel, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Box, Alert, CircularProgress,
  Paper, Chip
} from "@mui/material";
import { Download, Visibility, Close, PictureAsPdf, Print } from "@mui/icons-material";

import FormContainer from "../../../../../components/Forms/FormContainer2";
import useDecodedUser from "../../../../../services/hooks/useDecodedUser";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../../services/api/ExecuteApiRequests";
import { IPDCToastMessageResult } from "../../../../../components/Controls";
import config from "../../../../../config";

const RegisterComment = () => {
  const decodedUser = useDecodedUser();
  // Get token from localStorage or your auth mechanism
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const [requestNumber, setRequestNumber] = useState("");
  const [requestNumbers, setRequestNumbers] = useState([]);
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: "info", text: "" });
  const [toastMessage, setToastMessage] = useState({ message: "", type: "" });
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // State for modal and file info
  const [modalOpen, setModalOpen] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [fetchingFile, setFetchingFile] = useState(false);
  const [buildingPermitDetails, setBuildingPermitDetails] = useState(null);
  const [availableFiles, setAvailableFiles] = useState([]);

  useEffect(() => {
    // Fetch request numbers from the API
    GetDataFromApiWithParams("buildingPermits")
      .then((responseData) => {
        setRequestNumbers(responseData);
        console.log("Request numbers:", responseData);
      })
      .catch((error) => {
        console.error("Error fetching request numbers", error);
        setToastMessage({ message: "Error fetching request numbers", type: "error" });
      });
  }, []);

  // Fetch building permit details when request number changes
  useEffect(() => {
    const fetchBuildingPermitDetails = async () => {
      if (requestNumber) {
        try {
          setFetchingFile(true);
          // Try different endpoints to get the details
          let response;
          try {
            // First try with id
            response = await GetDataFromApiWithParams(`occupancyPermits/${requestNumber}/details`);
          } catch (error) {
            // If that fails, try with the regular endpoint
            response = await GetDataFromApiWithParams(`occupancyPermits/${requestNumber}`);
          }
          
          if (response) {
            console.log("Building permit details:", response);
            
            // Handle both array and single object responses
            const details = Array.isArray(response) ? response[0] : response;
            console.log("Building permit details:", details)
            setBuildingPermitDetails(details);
          }
        } catch (error) {
          console.error("Error fetching building permit details:", error);
          setMessage({ type: "error", text: "Error fetching building permit details" });
          setToastMessage({ message: "Error fetching building permit details", type: "error" });
        } finally {
          setFetchingFile(false);
        }
      }
    };

    fetchBuildingPermitDetails();
  }, [requestNumber]);

  const handleSubmitComment = async () => {
    if (!comment.trim()) {
      setMessage({ type: "warning", text: "Please enter a comment" });
      setToastMessage({ message: "Please enter a comment", type: "warning" });
      return;
    }
    
    setLoading(true);
    try {
      const responseData = await ExecutePostWithParams(`buildingPermits/${requestNumber}/comment/occ`, {
        requestNumber,
        commentedBy: decodedUser.id,
        comment,
      });
      setMessage({ type: "success", text: responseData.message || "Comment submitted successfully" });
      setToastMessage({ message: responseData.message || "Comment submitted successfully", type: "success" });
      setComment("");
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Error submitting comment" });
      setToastMessage({ message: error.message || "Error submitting comment", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer title="Professional Comment Submition" onSubmit={(e) => e.preventDefault()}>
      <Grid item xs={12}>
        <FormControl fullWidth={true}>
          <InputLabel>Request Number</InputLabel>
          <Select
            value={requestNumber}
            onChange={(e) => {
              setRequestNumber(e.target.value);
            }}
            error={!!errors.requestNumber}
          >
            {requestNumbers.map((num) => (
              <MenuItem key={num.id} value={num.id}>
                Building Permit Id:- {num.id} - Agreement Id:- {num.agreement_id} (Company Name:- {num.company})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      {/* Comment type and download controls removed */}

      {/* Available Files Info removed */}

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => setShowDetail(!showDetail)}
            disabled={!requestNumber}
            startIcon={<Visibility />}
          >
            {showDetail ? "HIDE DETAIL PLAN" : "VIEW DETAIL PLAN"}
          </Button>
          
          {buildingPermitDetails && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
              {buildingPermitDetails.agreement_id}
            </Typography>
          )}
          
          {/* View PDF button removed */}
        </Box>
      </Grid>

      {showDetail && (
        <Grid item xs={12}>
          <DetailPlanView permitRequestNumber={requestNumber} />
        </Grid>
      )}

      {/* Comment box and submit button */}
      <Grid item xs={12}>
        <TextField
          label="Add your comment"
          multiline
          rows={4}
          fullWidth={true}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          error={!!errors.comment}
          helperText={errors.comment}
        />
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={handleSubmitComment}
          disabled={loading || !requestNumber || !comment.trim()}
          sx={{ mt: 1 }}
        >
          {loading ? <CircularProgress size={24} /> : "SUBMIT COMMENT"}
        </Button>
      </Grid>

      {/* Alert Message */}
      {/* {message.text && (
        <Grid item xs={12}>
          <Alert 
            severity={message.type || "info"}
            onClose={() => setMessage({ type: "info", text: "" })}
          >
            {message.text}
          </Alert>
        </Grid>
      )} */}

      {/* Toast Message */}
      {toastMessage.message && (
        <Grid item xs={12}>
          <IPDCToastMessageResult 
            message={toastMessage.message} 
            type={toastMessage.type} 
          />
        </Grid>
      )}

      {/* PDF Preview Modal removed */}
    </FormContainer>
    
  );
};

export default RegisterComment;